import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TimesheetProvider } from './contexts/TimesheetContext';
import { PersonaProvider } from './contexts/PersonaContext';
import { DataSourceProvider } from './contexts/DataSourceContext';
import './utils/clearCache'; // Import to make clearTimesheetCache available globally
import { TimesheetGrid } from './components/TimesheetGrid';
import { ApprovalDashboard } from './components/ApprovalDashboard';
import { Sidebar } from './components/Navigation/Sidebar';
import { Header } from './components/Navigation/Header';
import { PersonaDashboard } from './components/dashboard/PersonaDashboard';
import { PlaceholderPage } from './components/Pages/PlaceholderPage';
import { AlertsDemoPage } from './components/Pages/AlertsDemoPage';
import { TimesheetReportsPage } from './components/Pages/TimesheetReportsPage';
import { UtilisationReportPage } from './components/Pages/UtilisationReportPage';
import { SettingsPage } from './components/Pages/SettingsPage';
import { ScreenSettingsPage } from './components/Pages/ScreenSettingsPage';
import { RolesAndRatesPage } from './components/Pages/RolesAndRatesPage';
import { RolesAndRatesPageSimple } from './components/Pages/RolesAndRatesPageSimple';
import { ActiveProjectsPage } from './components/Pages/ActiveProjectsPage';
import { TeamMembersPage } from './components/Pages/TeamMembersPage';
import { DocumentationPage } from './components/Pages/DocumentationPage';

import { CompaniesPage } from './components/Pages/CompaniesPage';

import { ApprovalWorkflowSettings } from './components/Pages/ApprovalWorkflowSettings';


import { Toaster } from '@/components/ui/sonner';
import { WelcomeModal } from '@/components/ProductTour';
import {
  BarChart3,
  Users,
  Briefcase,
  Zap,
  Download,
  Settings,
  HelpCircle,
  TrendingUp,
} from 'lucide-react';

// Navigation state types
type TimesheetDrilldownState = {
  employeeId: string;
  weekEnding: Date;
};

type AppNavigationState = {
  timesheetDrilldown?: TimesheetDrilldownState;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navigationState, setNavigationState] = useState<AppNavigationState | null>(null);

  const currentPath = location.pathname;

  const handleNavigate = (path: string, state?: AppNavigationState) => {
    setNavigationState(state ?? null);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar currentPath={currentPath} onNavigate={handleNavigate} />
      
      {/* Main content wrapper with left padding to account for fixed sidebar */}
      <div className="lg:pl-64 transition-all duration-300">
        <Header currentPath={currentPath} onNavigate={handleNavigate} />
        
        {/* Main content with top padding to account for sticky header */}
        <main className="pt-12 sm:pt-14 lg:pt-16 px-2 sm:px-3 lg:px-3 xl:px-4 pb-2 sm:pb-3 lg:pb-3 xl:pb-4">
          <div className="w-full max-w-[2000px] mx-auto">
            <Routes>
              {/* Dashboard */}
              <Route path="/" element={<PersonaDashboard onNavigate={handleNavigate} />} />
              
              {/* Timesheet */}
              <Route path="/timesheet" element={<TimesheetGrid selection={navigationState?.timesheetDrilldown} />} />
              
              {/* Approvals */}
              <Route path="/approvals" element={<ApprovalDashboard />} />
              
              {/* Reports */}
              <Route path="/reports/timesheet" element={<TimesheetReportsPage onNavigate={handleNavigate} />} />
              <Route path="/reports/utilization" element={<UtilisationReportPage />} />
              <Route
                path="/reports/billable"
                element={
                  <PlaceholderPage
                    title="Billable Hours"
                    description="Billable vs non-billable analysis"
                    icon={BarChart3}
                  />
                }
              />
              
              {/* Analytics */}
              <Route
                path="/analytics"
                element={
                  <PlaceholderPage
                    title="Analytics"
                    description="Advanced analytics and insights"
                    icon={TrendingUp}
                  />
                }
              />
              
              {/* Team */}
              <Route path="/team/members" element={<TeamMembersPage />} />
              <Route
                path="/team/schedule"
                element={
                  <PlaceholderPage
                    title="Team Schedule"
                    description="View team availability and schedules"
                    icon={Users}
                  />
                }
              />
              <Route
                path="/team/capacity"
                element={
                  <PlaceholderPage
                    title="Capacity Planning"
                    description="Resource capacity planning and forecasting"
                    icon={TrendingUp}
                  />
                }
              />
              
              {/* Projects */}
              <Route path="/projects/active" element={<ActiveProjectsPage />} />
              <Route
                path="/projects/archive"
                element={
                  <PlaceholderPage
                    title="Archived Projects"
                    description="View completed and archived projects"
                    icon={Briefcase}
                  />
                }
              />
              <Route
                path="/projects/budget"
                element={
                  <PlaceholderPage
                    title="Budget Tracking"
                    description="Project budget and forecasting"
                    icon={Briefcase}
                  />
                }
              />
              
              {/* Companies & Integrations */}
              <Route path="/integrations" element={<CompaniesPage />} />
              {/* Legacy integration routes - redirect to main companies page */}
              <Route path="/integrations/erp" element={<Navigate to="/integrations" replace />} />
              <Route path="/integrations/hr" element={<Navigate to="/integrations" replace />} />
              <Route path="/integrations/sync" element={<Navigate to="/integrations" replace />} />
              
              {/* Data */}
              <Route
                path="/data/import"
                element={
                  <PlaceholderPage
                    title="Import Data"
                    description="Import timesheet data from external sources"
                    icon={Download}
                  />
                }
              />
              <Route
                path="/data/export"
                element={
                  <PlaceholderPage
                    title="Export Data"
                    description="Export data to CSV, Excel, or PDF"
                    icon={Download}
                  />
                }
              />
              
              {/* Settings */}
              <Route path="/settings/general" element={<SettingsPage onNavigate={handleNavigate} />} />
              <Route path="/settings/screens" element={<ScreenSettingsPage />} />
              <Route path="/settings/roles" element={<RolesAndRatesPage onNavigate={handleNavigate} />} />
              <Route path="/settings/approval" element={<ApprovalWorkflowSettings />} />
              <Route
                path="/settings/notifications"
                element={
                  <PlaceholderPage
                    title="Notifications"
                    description="Manage notification preferences"
                    icon={Settings}
                  />
                }
              />
              <Route
                path="/settings/security"
                element={
                  <PlaceholderPage
                    title="Security"
                    description="Security settings and permissions"
                    icon={Settings}
                  />
                }
              />
              
              {/* Help */}
              <Route path="/help/docs" element={<DocumentationPage onNavigate={handleNavigate} />} />
              <Route
                path="/help/support"
                element={
                  <PlaceholderPage
                    title="Contact Support"
                    description="Get help from our support team"
                    icon={HelpCircle}
                  />
                }
              />
              
              {/* Demo Pages */}
              <Route path="/demo/alerts" element={<AlertsDemoPage />} />
              
              {/* Fallback route */}
              <Route path="*" element={<PersonaDashboard onNavigate={handleNavigate} />} />
            </Routes>
          </div>
        </main>
      </div>

      {/* Welcome Modal for first-time users */}
      <WelcomeModal tourId="welcome-tour" autoShow={true} />

      <Toaster />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <DataSourceProvider>
        <TimesheetProvider>
          <PersonaProvider isManager={true}>
            <AppContent />
          </PersonaProvider>
        </TimesheetProvider>
      </DataSourceProvider>
    </BrowserRouter>
  );
}
